// Serviço para gerar agentes automaticamente usando IA

interface GeneratedAssistant {
  name: string;
  description: string;
  instructions: string;
  avatar: string;
  category: string;
  tags: string[];
}

const GENERATOR_PROMPT = `Você é um especialista em criar agentes de IA personalizados. Com base na descrição do usuário, gere um agente completo e bem estruturado.

IMPORTANTE: Responda APENAS com um JSON válido, sem markdown, sem explicações, apenas o JSON puro.

O JSON deve ter exatamente esta estrutura:
{
  "name": "Nome curto e descritivo do agente (máximo 30 caracteres)",
  "description": "Descrição breve do que o agente faz (máximo 100 caracteres)",
  "instructions": "Instruções detalhadas - USE \\n\\n para separar parágrafos e seções",
  "avatar": "Um único emoji que represente bem o agente",
  "category": "Uma das categorias: productivity, communication, creativity, analysis, support, development, education, other",
  "tags": ["array", "de", "tags", "relevantes", "máximo", "5"]
}

FORMATO DAS INSTRUÇÕES (use \\n\\n entre seções):
As instruções DEVEM ser bem formatadas com quebras de linha (\\n\\n) entre cada seção:

## PAPEL
[Descrição clara do papel do agente]

## TOM DE VOZ
[Especificar: formal, casual, técnico, amigável, etc.]

## COMPETÊNCIAS
[Lista das principais competências e especialidades]

## FORMATO DAS RESPOSTAS
[Como deve estruturar as respostas]

## EXEMPLOS DE COMPORTAMENTO
[Exemplos práticos de como deve agir]

## LIMITAÇÕES
[O que NÃO deve fazer]

LEMBRE-SE: 
- Use \\n\\n no JSON para criar quebras de linha nas instruções
- Retorne APENAS o JSON, nada mais`;

export async function generateAssistantWithAI(userDescription: string): Promise<GeneratedAssistant> {
  const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
  
  if (!apiKey) {
    throw new Error('API Key da OpenAI não configurada');
  }

  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content: GENERATOR_PROMPT
        },
        {
          role: 'user',
          content: `Crie um agente de IA com base nesta descrição: "${userDescription}"`
        }
      ],
      temperature: 0.7,
      max_tokens: 2000
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Erro ao gerar agente');
  }

  const data = await response.json();
  const content = data.choices[0]?.message?.content;

  if (!content) {
    throw new Error('Resposta vazia da IA');
  }

  // Limpar possíveis marcadores de markdown
  let cleanContent = content.trim();
  if (cleanContent.startsWith('```json')) {
    cleanContent = cleanContent.slice(7);
  }
  if (cleanContent.startsWith('```')) {
    cleanContent = cleanContent.slice(3);
  }
  if (cleanContent.endsWith('```')) {
    cleanContent = cleanContent.slice(0, -3);
  }
  cleanContent = cleanContent.trim();

  try {
    const generated = JSON.parse(cleanContent) as GeneratedAssistant;
    
    // Validar campos obrigatórios
    if (!generated.name || !generated.description || !generated.instructions) {
      throw new Error('Agente gerado está incompleto');
    }

    // Processar instruções para garantir formatação adequada
    let instructions = generated.instructions;
    
    // Se não tiver quebras de linha, adicionar formatação básica
    if (!instructions.includes('\n')) {
      // Tentar adicionar quebras antes de seções comuns
      instructions = instructions
        .replace(/\. (Seu |O |A |As |Os |Este |Esta |Você |Ele |Ela )/g, '.\n\n$1')
        .replace(/(Competências|Especialidades|Tom de voz|Formato|Exemplos|Limitações|Comportamento):/gi, '\n\n**$1:**')
        .replace(/(\d+\.\s)/g, '\n$1');
    }

    // Garantir valores padrão
    return {
      name: generated.name.slice(0, 50),
      description: generated.description.slice(0, 200),
      instructions: instructions.trim(),
      avatar: generated.avatar || '🤖',
      category: generated.category || 'productivity',
      tags: Array.isArray(generated.tags) ? generated.tags.slice(0, 5) : []
    };
  } catch (parseError) {
    console.error('Erro ao parsear resposta da IA:', content);
    throw new Error('Erro ao processar resposta da IA');
  }
}
